import { z } from "zod";
import { RubricDimensionSchema } from "./primitives.js";
/**
 * PRACTICE (coach plan steps 6, 7 and 8; C09, C06, C07): a small authored
 * exercise a learner can try between engagements, with an observable
 * finishing condition, a worked revision on request, and a short resumable
 * dialogue around it.
 *
 * THREE THINGS THIS CONTRACT KEEPS APART, because the plan says they differ:
 * a brief (authored, versioned, what the exercise IS), the events (what
 * happened, append-only, the only thing persisted), and the state (a
 * projection of the events by the engine, never stored as truth). Suggested,
 * accepted, attempted, revised and concluded are different events, so memory
 * (C08 later) can tell them apart without inference.
 *
 * Learner-facing text carries no digits: these words reach the Home and
 * Record surfaces, where the no-numeral rule holds.
 */
const noDigits = (text) => !/\d/.test(text);
const Learnerfacing = (max) => z.string().min(1).max(max).refine(noDigits, { message: "learner-facing practice text carries no digits" });
const Slug = z.string().regex(/^[a-z][a-z0-9-]{1,63}$/);
/**
 * One observable thing the attempt must contain. Presence is proven by a
 * quotation from the attempt that code has verified is really in it; the
 * markers are the deterministic fallback when no model reading is available
 * (Law 4): a word or phrase whose presence in the attempt suggests the
 * criterion. Quality beyond presence stays descriptive.
 */
export const PracticeCriterionSchema = z.strictObject({
    id: Slug,
    /** What the learner must produce, as something a reader could point at. */
    text: Learnerfacing(200),
    /** Lower-case words or phrases for the heuristic reading; at least one. */
    markers: z.array(z.string().min(2).max(40)).min(1).max(8),
});
/** A bounded counterpart the coach may play for a dialogue brief (C07). Authored lines are the fallback voice. */
export const PracticeCounterpartSchema = z.strictObject({
    name: Learnerfacing(60),
    role: Learnerfacing(120),
    /** How they behave, for the model that plays them and for the learner to know what they are facing. */
    stance: Learnerfacing(400),
    /** Replies code can use in order when the model is unavailable; the last one repeats. */
    authored_lines: z.array(Learnerfacing(400)).min(2).max(6),
});
export const PracticeVariationSchema = z.strictObject({
    id: Slug,
    /** The changed situation for a later attempt, so the behaviour is practised in the learner's own words again. */
    situation: Learnerfacing(700),
});
export const PRACTICE_MAX_CRITERIA = 4;
export const PracticeBriefSchema = z.strictObject({
    id: Slug,
    version: z.number().int().min(1),
    dimension: RubricDimensionSchema,
    title: Learnerfacing(80),
    /** The behaviour being practised, in one sentence. */
    target_behaviour: Learnerfacing(200),
    /** Why it is worth practising, in one or two sentences. */
    why: Learnerfacing(400),
    /** A short situation, pedagogical, never a fragment of an unpublished case. */
    situation: Learnerfacing(900),
    /** What the learner is asked to write or say. */
    task: Learnerfacing(400),
    criteria: z.array(PracticeCriterionSchema).min(2).max(PRACTICE_MAX_CRITERIA),
    /** Phrasings and shapes that also satisfy the task, so the reading is not a formula. */
    acceptable_variations: z.array(Learnerfacing(200)).max(4),
    /** One hint, shown on request. */
    hint: Learnerfacing(300),
    /** One worked example, clearly hypothetical, shown on request; never attributed to the learner. */
    example: Learnerfacing(700),
    /** What finishing looks like: producing the elements, not proving mastery. */
    finishing: Learnerfacing(300),
    variations: z.array(PracticeVariationSchema).max(3),
    counterpart: PracticeCounterpartSchema.nullable(),
});
export const PracticeOriginSchema = z.enum(["LEARNER_ASKED", "PLAN", "COACH_CONTROL"]);
/** One verified reading of one criterion: the quote is really in the attempt, or nothing was found. */
export const PracticeCriterionReadingSchema = z.strictObject({
    id: Slug,
    quote: z.string().min(1).max(400).nullable(),
});
const eventBase = { seq: z.number().int().min(1), at: z.iso.datetime() };
/**
 * The events. Append-only; the engine projects them. `READ` always follows
 * the `ATTEMPTED` it reads, and carries where the reading came from: a model
 * whose quotes code verified, or the markers.
 */
export const PracticeEventSchema = z.discriminatedUnion("type", [
    z.strictObject({ type: z.literal("OFFERED"), ...eventBase, brief_id: Slug, brief_version: z.number().int().min(1), origin: PracticeOriginSchema, reason: z.string().min(1).max(300) }),
    z.strictObject({ type: z.literal("ACCEPTED"), ...eventBase }),
    z.strictObject({ type: z.literal("DECLINED"), ...eventBase }),
    z.strictObject({ type: z.literal("ATTEMPTED"), ...eventBase, attempt_index: z.number().int().min(1), text: z.string().min(1).max(4000), revises: z.number().int().min(1).nullable(), idempotency_key: z.string().min(1).max(120).nullable() }),
    z.strictObject({ type: z.literal("READ"), ...eventBase, attempt_index: z.number().int().min(1), criteria: z.array(PracticeCriterionReadingSchema).min(1), source: z.enum(["model", "heuristic"]) }),
    z.strictObject({ type: z.literal("HINT_SHOWN"), ...eventBase }),
    z.strictObject({ type: z.literal("EXAMPLE_SHOWN"), ...eventBase }),
    /** C06: a coach-authored revision of the learner's own words, stored as its own kind of text. */
    z.strictObject({ type: z.literal("REVISION_SHOWN"), ...eventBase, attempt_index: z.number().int().min(1), original_excerpt: z.string().min(1).max(1200), revision: z.string().min(1).max(1200), change: z.string().min(1).max(400), source: z.enum(["model", "authored"]) }),
    z.strictObject({ type: z.literal("VARIATION_OFFERED"), ...eventBase, variation_id: Slug }),
    /** C07: the bounded counterpart's line, in reply to an attempt. */
    z.strictObject({ type: z.literal("COUNTERPART_SAID"), ...eventBase, attempt_index: z.number().int().min(1), text: z.string().min(1).max(800), source: z.enum(["model", "authored"]) }),
    z.strictObject({ type: z.literal("PAUSED"), ...eventBase }),
    z.strictObject({ type: z.literal("RESUMED"), ...eventBase }),
    z.strictObject({ type: z.literal("CONCLUDED"), ...eventBase, by: z.enum(["LEARNER", "CRITERIA_MET", "LEARNER_EARLY"]) }),
]);
export const PracticeStageSchema = z.enum(["OFFERED", "ACCEPTED", "ATTEMPTED", "PAUSED", "CONCLUDED", "DECLINED"]);
export const PracticeCriterionStatusSchema = z.enum(["PRESENT", "NOT_FOUND", "UNREAD"]);
export const PracticeAttemptSchema = z.strictObject({
    index: z.number().int().min(1),
    text: z.string().min(1),
    revises: z.number().int().min(1).nullable(),
    /** Per criterion: the verified quote, null when not found, absent until read. */
    reading: z.record(Slug, z.string().nullable()).nullable(),
    reading_source: z.enum(["model", "heuristic"]).nullable(),
    counterpart_line: z.string().nullable(),
});
/** The projection. Never stored as truth: the events are. */
export const PracticeStateSchema = z.strictObject({
    brief_id: Slug,
    brief_version: z.number().int().min(1),
    dimension: RubricDimensionSchema,
    origin: PracticeOriginSchema,
    stage: PracticeStageSchema,
    /** The stage a pause interrupted, so resume returns there. */
    stage_before_pause: PracticeStageSchema.nullable(),
    attempts: z.array(PracticeAttemptSchema),
    /** Status per criterion over the LATEST read attempt. */
    criteria: z.record(Slug, PracticeCriterionStatusSchema),
    hint_shown: z.boolean(),
    example_shown: z.boolean(),
    revision: z.strictObject({ attempt_index: z.number().int().min(1), original_excerpt: z.string(), revision: z.string(), change: z.string(), source: z.enum(["model", "authored"]) }).nullable(),
    variation_id: Slug.nullable(),
    concluded_by: z.enum(["LEARNER", "CRITERIA_MET", "LEARNER_EARLY"]).nullable(),
    /** True once every criterion is PRESENT on the latest read attempt: the finishing condition, not a mastery claim. */
    finished: z.boolean(),
    last_seq: z.number().int().min(0),
});
//# sourceMappingURL=practice.js.map